import * as z from 'zod'
import { extraZ, moneyZ, partyZ, pricedTaxZ, selectedExtrasZ } from '../schema'

export const timeZ = z
  .object({
    hour: z.number(),
    minute: z.number(),
    timezone: z.string(),
  })
  .nullable()
  .optional()

const arrivalTaxZ = z.object({
  amount: moneyZ,
  applicable: z.boolean(),
  maxNights: z.number().nullable(),
  minAge: z.number().nullable(),
  text: z.string(),
  type: z.string(),
})

export type BookingArrivalTax = z.infer<typeof arrivalTaxZ>

export const bookingInfoZ = z.object({
  address: z.string(),
  ageWarning: z.string().nullable(),
  allowImmediatePayment: z.boolean(),
  allowedSubcategories: z.string().array(),
  arrivalFrom: z.string(),
  arrivalFromObj: z
    .object({
      hour: z.number(),
      minute: z.number(),
      timezone: z.string(),
    })
    .optional()
    .nullable(),
  arrivalTo: z.string(),
  arrivalToObj: z
    .object({
      hour: z.number(),
      minute: z.number(),
      timezone: z.string(),
    })
    .optional()
    .nullable(),
  arrivalTime: z.string(),
  covidMeasures: z.string(),
  campsiteName: z.string(),
  campsiteTermsAndConditions: z.string(),
  campsiteTimezone: z.string(),
  cancellationPolicy: z.string(),
  cancellationPolicyNeedsTranslation: z.boolean().optional(),
  categories: z
    .object({
      isPositive: z.boolean(),
      name: z.string(),
      slug: z.string(),
    })
    .array(),
  departFrom: z.string(),
  dimensions: z.string().nullable(),
  disallowedSubcategories: z.string().array(),
  dimensionsType: z
    .enum(['max', 'avg', 'min', 'unlimited', 'custom', ''])
    .optional()
    .nullable(),
  dimensionsWidth: z.string().optional().nullable(),
  dimensionsDepth: z.string().optional().nullable(),
  dimensionsDetailed: z
    .object({
      areaFt: z.number().nullable(),
      areaM: z.number().nullable(),
      customDimensions: z.string().nullable(),
      depthFt: z.number().nullable(),
      depthM: z.number().nullable(),
      dimensionsType: z.string().nullable(),
      dimensionsTypeCode: z.string().nullable(),
      widthFt: z.number().nullable(),
      widthM: z.number().nullable(),
    })
    .optional()
    .nullable(),
  enforceSpecialRequests: z.boolean(),
  oneUnitPerPitch: z.boolean(),
  isByo: z.boolean(),
  lastBooked: z.string().nullable(),
  maxChildAge: z.number(),
  notices: z.string(),
  noticesNeedsTranslation: z.boolean().optional(),
  campsiteNotices: z
    .object({
      isCovid: z.boolean(),
      text: z.string(),
    })
    .array(),
  path: z.string(),
  paymentTiming: z.string(),
  photo: z
    .object({
      caption: z.string().optional(),
      url: z.object({
        masterImage: z.string(),
      }),
    })
    .optional(),
  pitchTypeDimensions: z.string().nullable(),
  pitchTypeDimensionsExample: z.string().nullable().optional(),
  pitchTypeName: z.string(),
  rating: z.object({
    rate: z.number(),
    votes: z.number(),
  }),
  requireCarRegistration: z.boolean(),
  isEstimatedTimeOfArrivalCompulsory: z.boolean().optional(),
  requireEstimatedTimeOfArrival: z.boolean(),
  requirePartyNames: z.boolean(),
  requestGroupName: z.boolean(),
  slug: z.string(),
  subcategoriesRequiringDimensions: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .optional(),
  taxes: z.object({
    arrival: arrivalTaxZ.array(),
    city: z.string(),
    exclusive: z
      .object({
        name: z.string(),
        rate: z.number(),
      })
      .array(),
  }),
  termsAndConditionsNeedsTranslation: z.boolean().optional(),
  termsUsefulInfo: z.string().array(),
  usefulInfo: z.string(),
  warnings: z.string().array(),
})
export type BookingInfo = z.infer<typeof bookingInfoZ>

const quoteArrivalTaxZ = z.object({
  amount: moneyZ,
  isHidden: z.boolean(),
  type: z.string(),
})

export const bookingQuoteZ = z.object({
  refundUntilDate: z.string().nullable(),
  arrivalTaxes: quoteArrivalTaxZ.array(),
  balanceDueDate: z.string().nullable(),
  chargetypeId: z.string(),
  commission: z.number(),
  convertedChildren: z.number(),
  deposit: moneyZ,
  extras: extraZ.array(),
  // key is the extra id, and value is the quantity
  extrasInitial: z.record(z.number()),
  compulsoryExtras: extraZ.array(),
  pitchesToSell: z.number(),
  priceAccommodation: moneyZ,
  priceTotal: moneyZ,
  remainder: moneyZ,
  paypalEnabled: z.boolean(),
  paymentWhen: z.enum([
    'pitchup_only',
    'stripe',
    'deferred_payment',
    'immediate_payment',
  ]),
  pricedTax: pricedTaxZ.array(),
  convertedParty: z.object({
    adults: z.number(),
    children: z.number(),
    childrenAges: z.number().array(),
    infants: z.number(),
    infantsAges: z.number().array(),
  }),
})

export const bookingDataZ = z.object({
  info: bookingInfoZ,
  quote: bookingQuoteZ.nullable(),
  stripeToken: z
    .object({
      customer_id: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
})

export type BookingData = z.infer<typeof bookingDataZ>

const dateRange = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
})

export const dataRequestParamsZ = z.object({
  stayRange: dateRange,
  party: partyZ,
  pitchtypeId: z.string(),
  chargetypeId: z.string().optional(),
  selectedExtras: selectedExtrasZ,
  slug: z.string(),
})
export type DataRequestParams = z.infer<typeof dataRequestParamsZ>

export const bookingDataEndpoint = z
  .function()
  .args(dataRequestParamsZ)
  .returns(z.promise(bookingDataZ.nullable()))
